import { computed, Injectable, OnDestroy, signal } from '@angular/core';
import { fromEvent, Subject, takeUntil } from 'rxjs';

/**
 * Platform provider.
 * Detects and tracks browser window size changes for responsive behavior.
 *
 * This provider is global (providedIn: 'root') and can be used throughout the application.
 *
 * @example
 * ```typescript
 * // In a component
 * private readonly platform = inject(PlatformProvider);
 *
 * // Using isScreenXS (< 640px)
 * public readonly stickyActions = computed(() => !this.platform.isScreenXS());
 *
 * // Using isScreenSMX (<= 1024px)
 * constructor() {
 *   effect(() => {
 *     if (this.platform.isScreenSMX()) {
 *       // Mobile/tablet layout
 *     } else {
 *       // Desktop layout
 *     }
 *   });
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class PlatformProvider implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  /**
   * Breakpoint for extra small screens (640px).
   * Used for mobile-first responsive behavior.
   */
  private readonly BREAKPOINT_XS = 640;

  /**
   * Breakpoint for small screens (1024px).
   * Matches @cm-breakpoint-sm-x from variables.less.
   */
  private readonly BREAKPOINT_SM_X = 1024;

  /**
   * Current window width in pixels.
   * Updates on window resize events.
   */
  private readonly _windowWidth = signal<number>(this.getWindowWidth());
  public readonly windowWidth = this._windowWidth.asReadonly();

  /**
   * Whether the screen width is < 640px (extra small screens).
   * True for mobile, false for tablet/desktop.
   * Computed from windowWidth signal.
   */
  public readonly isScreenXS = computed(() => this.windowWidth() < this.BREAKPOINT_XS);

  /**
   * Whether the screen width is <= 1024px (small screens).
   * True for mobile/tablet, false for desktop.
   * Computed from windowWidth signal.
   */
  public readonly isScreenSMX = computed(() => this.windowWidth() <= this.BREAKPOINT_SM_X);

  constructor() {
    this.initializeWindowResizeListener();
  }

  /**
   * Gets the current window width safely (handles SSR).
   */
  private getWindowWidth(): number {
    if (typeof window !== 'undefined') {
      return window.innerWidth;
    }
    return 0;
  }

  /**
   * Initializes the window resize event listener.
   * Updates windowWidth signal on every resize event.
   */
  private initializeWindowResizeListener(): void {
    if (typeof window === 'undefined') return;

    fromEvent(window, 'resize')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this._windowWidth.set(this.getWindowWidth());
      });
  }

  /**
   * Scrolls smoothly to a target element within its scrollable container.
   *
   * Handles cases where the scroll is in the page's host element (overflow-y: auto on :host)
   * rather than the window. Calculates positions relative to the scroll container and
   * applies a smooth scroll with an optional offset from the top.
   *
   * @param targetElement - The element to scroll to
   * @param hostElement - The scrollable container element (usually the page host)
   * @param options - Optional configuration
   * @param options.offset - Pixels from top to offset the scroll position (default: 80)
   * @param options.delay - Milliseconds to wait before scrolling (default: 300)
   * @param options.behavior - Scroll behavior: 'smooth' or 'auto' (default: 'smooth')
   *
   * @example
   * ```typescript
   * private readonly platform = inject(PlatformProvider);
   * private readonly elementRef = inject(ElementRef);
   * private readonly tableCard = viewChild<ElementRef<HTMLElement>>('tableCard');
   *
   * scrollToTable(): void {
   *   const target = this.tableCard();
   *   const host = this.elementRef.nativeElement;
   *
   *   if (target?.nativeElement) {
   *     this.platform.scrollTo(target.nativeElement, host);
   *   }
   * }
   * ```
   */
  /**
   * Toggles a CSS class on the first element matching the given selector.
   * Safe to call before the element exists (returns false) and during SSR.
   *
   * @param selector - CSS selector for the target element
   * @param className - Class name to toggle
   * @param force - When true, adds the class; when false, removes it
   * @returns True if the element was found and updated, false otherwise
   */
  public toggleElementClass(selector: string, className: string, force: boolean): boolean {
    if (typeof document === 'undefined') return false;
    const element = document.querySelector(selector);
    if (!element) return false;
    element.classList.toggle(className, force);
    return true;
  }

  public scrollTo(
    targetElement: HTMLElement,
    hostElement: HTMLElement,
    options?: {
      offset?: number;
      delay?: number;
      behavior?: ScrollBehavior;
    }
  ): void {
    const { offset = 80, delay = 300, behavior = 'smooth' } = options ?? {};

    setTimeout(() => {
      if (!targetElement || !hostElement) return;

      const hostRect = hostElement.getBoundingClientRect();
      const targetRect = targetElement.getBoundingClientRect();
      const currentScroll = hostElement.scrollTop;
      const targetScrollPosition = currentScroll + (targetRect.top - hostRect.top) - offset;

      hostElement.scrollTo({
        top: targetScrollPosition,
        behavior,
      });
    }, delay);
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
